import React, {useEffect, useState} from "react";
import {api} from "../utils/gashtaltAPI";
import Navbar from "../components/Navbar";
import ErrorPage from "../components/ErrorPage";
import Loader from "../components/Loader";
import TableToolbar from "../components/TableToolbar";
import {toast} from "react-toastify";
import {filterNonNumeric, formatNumbers, toEnglishNumber, toPersianNumber} from "../utils/numberConversion";
import {
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    useDisclosure,
} from "@chakra-ui/react";

function AddInvoice() {
    const [products, setProducts] = useState([]);
    const [productIndex, setProductIndex] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [price, setPrice] = useState("");
    const [foundUser, setFoundUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const {isOpen, onOpen, onClose} = useDisclosure();

    const selectedProduct = productIndex === "" ? null : products[productIndex];

    useEffect(() => {
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const response = await api.get("/api/products");
                setProducts(response.data.data);
            } catch (error) {
                setError(error);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, []);

    const handleError = (error) => {
        if (error.response) {
            toast.error(error.response.data.message, {
                position: "bottom-right",
            });
        } else if (error.request) {
            toast.error("اتصال اینترنت خود را بررسی کنید!", {
                position: "bottom-right",
            });
        } else {
            toast.error("خطایی رخ داد!", {
                position: "bottom-right",
            });
        }
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        if (!selectedProduct || !phoneNumber) {
            toast.error("محصول و شماره همراه را وارد کنید!", {
                position: "bottom-right",
            });
            return;
        }
        setFoundUser(null);
        setSearching(true);
        try {
            const response = await api.post("/api/invoice/lookup", {
                product_id: selectedProduct.id,
                product_type: selectedProduct.product_type,
                phone_number: toEnglishNumber(phoneNumber),
            });
            setFoundUser(response.data.data);
        } catch (error) {
            handleError(error);
        } finally {
            setSearching(false);
        }
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const response = await api.post("/api/invoice/grant", {
                user_id: foundUser.user_id,
                product_id: selectedProduct.id,
                product_type: selectedProduct.product_type,
                price: price === "" ? null : toEnglishNumber(price),
            });
            toast.success(response.data.message, {
                position: "bottom-right",
            });
            setFoundUser(null);
            setPhoneNumber("");
            setPrice("");
            onClose();
        } catch (error) {
            handleError(error);
        } finally {
            setSubmitting(false);
        }
    };

    const finalPrice = price === "" ? foundUser?.product_price : toEnglishNumber(price);

    if (loading) return <Loader/>;
    if (error) return <ErrorPage error={error}/>;
    return (
        <div className="d-flex flex-column h-100">
            <Navbar/>
            <div className="admin-page flex-fill">
                <TableToolbar title="ثبت پرداخت دستی"/>

                <div className="admin-table-card p-4">
                    <form onSubmit={handleSearch}>
                        <div className="custom-input-group mb-3">
                            <label>محصول</label>
                            <select
                                className="form-control"
                                value={productIndex}
                                onChange={(e) => {
                                    setProductIndex(e.target.value);
                                    setFoundUser(null);
                                }}
                            >
                                <option value="">انتخاب محصول</option>
                                {products.map((product, index) => (
                                    <option key={`${product.product_type}-${product.id}`} value={index}>
                                        {product.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="custom-input-group mb-3">
                            <label>شماره همراه</label>
                            <input
                                type="text"
                                className="form-control"
                                value={phoneNumber}
                                onChange={(e) => {
                                    setPhoneNumber(filterNonNumeric(e.target.value));
                                    setFoundUser(null);
                                }}
                                placeholder="شماره همراه کاربر"
                            />
                        </div>
                        <div className="custom-input-group mb-3">
                            <label>مبلغ (اختیاری)</label>
                            <input
                                type="text"
                                className="form-control"
                                value={price}
                                onChange={(e) => setPrice(filterNonNumeric(e.target.value))}
                                placeholder="در صورت خالی بودن، قیمت محصول ثبت می‌شود"
                            />
                        </div>
                        <button type="submit" className="btn btn-outline-green" disabled={searching}>
                            {searching ? "در حال جستجو..." : "جستجو"}
                        </button>
                    </form>

                    {foundUser && (
                        <div className="mt-4 d-flex align-items-center gap-3 flex-wrap">
                            <span>
                                کاربر یافت شده: <strong>{foundUser.name}</strong> (
                                {toPersianNumber(foundUser.phone_number)})
                            </span>
                            {foundUser.already_paid ? (
                                <span className="text-danger">
                                    این کاربر قبلاً این محصول را خریداری کرده است!
                                </span>
                            ) : (
                                <button className="btn btn-outline-green" onClick={onOpen}>
                                    ثبت پرداخت برای این کاربر
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay/>
                <ModalContent>
                    <ModalHeader>ثبت پرداخت دستی</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody>
                        <p>
                            آیا از ثبت پرداخت محصول «{foundUser?.product_name}» به مبلغ{" "}
                            {toPersianNumber(formatNumbers(finalPrice ?? 0))} تومان برای {foundUser?.name} اطمینان
                            دارید؟ این محصول بلافاصله برای این کاربر فعال می‌شود.
                        </p>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme="gray" mr={3} onClick={onClose} marginX={"1rem"}>
                            بستن
                        </Button>
                        <Button colorScheme="green" onClick={handleSubmit} isDisabled={submitting}>
                            ثبت
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    );
}

export default AddInvoice;
